import moment from 'moment'

import { getFlightFareFamilies, searchFlights, type FareFamiliesResponse } from 'api/flights'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  FlightsClassTypes,
  isValidFareClassWithReward,
  toBaseFareClass,
  type FareClassWithReward,
} from 'constants/flight'
import { CHARTER } from 'constants/provider'
import { getFeederOriginAirportCode, isConfiguredFeederOriginAirportCode, isMaldivesCharterBundleOffer } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { isSkyLoungeSingleOccupancy } from 'lib/flights/flightUtils'
import { countOccupantsForFlights, mergeOccupants } from 'lib/offer/occupancyUtils'
import { getSeatsLeftFromJourney } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { extractDepartingAndReturningFaresForClass } from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import type { CharterFlightBundleSelection } from 'tripPlanner/types/charterFlightBundle'

export type { CharterFlightBundleSelection }

/**
 * Narrows an arbitrary route string (e.g. read off a persisted saved item) to a
 * known `App.CharterBundleRoute` so it can index `CHARTER_FLIGHT_DETAILS`. Kept
 * here (trip-planner charter checkout module) rather than the shared offer-page
 * util so this feature doesn't reach into offer-page code.
 */
export function isCharterBundleRoute(route: string): route is App.CharterBundleRoute {
  return route in CHARTER_FLIGHT_DETAILS
}

export { isMaldivesCharterBundleOffer }

// Defaults that only guard a missing or legacy saved charter bundle — a present
// bundle always reflects the user's own pick. MEL is the Maldives charter's
// departure airport; economy / 2 adults match the offer-page defaults. Shared by
// the display (cart + itinerary price) and checkout paths so the quoted price and
// the charged price always use identical fallbacks.
export const DEFAULT_CHARTER_ORIGIN_AIRPORT_CODE = 'MEL'
export const DEFAULT_CHARTER_OCCUPANCIES: Array<App.Occupants> = [{ adults: 2 }]

export interface ResolvedCharterBundle {
  offer: App.HotelOffer | App.HotelOfferSummary
  charterFlightBundle: CharterFlightBundleSelection
  occupancies: Array<App.Occupants>
}

/**
 * Single source of truth for "is this cart/itinerary item a priceable Maldives
 * charter bundle, and with what selection?". Used by BOTH the display price path
 * (useCharterCartFlightPrices / the itinerary tile) and the checkout injection
 * path, so the quoted price can never diverge from what checkout charges.
 *
 * Detection is driven off the live offer (campaign tag + region) — the same
 * authoritative signal checkout uses — with the persisted bundle only refining
 * the user's origin/fare/route, and occupancies falling back to the shared
 * default. `requireFlightsEnabled` defaults to false because the offer returned
 * by getOffersById doesn't reliably populate `offerFlightsEnabled`; keeping the
 * default identical on both paths is what guarantees display === charge.
 */
export function resolveCharterBundle(params: {
  offer: App.AnyOffer | undefined
  isMaldivesCharterEnabled: boolean
  persistedBundle: CharterFlightBundleSelection | undefined
  occupancies: Array<App.Occupants> | undefined
  requireFlightsEnabled?: boolean
}): ResolvedCharterBundle | null {
  const { offer, isMaldivesCharterEnabled, persistedBundle, occupancies, requireFlightsEnabled = false } = params
  if (!isMaldivesCharterBundleOffer(offer, isMaldivesCharterEnabled, { requireFlightsEnabled })) {
    return null
  }
  const effectiveOccupancies = occupancies?.length ? occupancies : DEFAULT_CHARTER_OCCUPANCIES
  // Derive the route's origin and the offer's allowed default cabin from the shared
  // charter policy helpers rather than hard-coding MEL/economy, so a business-only or
  // otherwise constrained charter offer defaults correctly.
  const route = persistedBundle?.charterBundleRoute ?? MALDIVES_CHARTER_ROUTE
  const routeOriginAirportCode = isCharterBundleRoute(route)
    ? CHARTER_FLIGHT_DETAILS[route].originAirport.airportCode
    : undefined

  // Validate the persisted cabin against the *live* offer policy rather than
  // trusting it blindly. Saved state goes stale: the offer can be re-tagged to
  // different cabins, or the occupancy may no longer be SkyLounge-compatible, so a
  // globally-valid saved cabin the current offer disallows must not search/checkout
  // anyway. Mirror the offer page (`resolveAllowedFareClass`): coerce a valid-but-
  // disallowed cabin to the policy default, keeping the persisted selection (incl.
  // any reward variant) when its base cabin survives. A wholly invalid saved value
  // is passed through unchanged so the downstream validity guard rejects it instead
  // of us silently substituting a cabin.
  const persistedFareClass = persistedBundle?.fareClass
  const persistedBaseFareClass = persistedFareClass && isValidFareClassWithReward(persistedFareClass)
    ? toBaseFareClass(persistedFareClass)
    : undefined
  const policyFareClass = resolveAllowedFareClass({
    selectedFareClass: persistedBaseFareClass,
    campaignTags: offer.campaignTags,
    occupancies: effectiveOccupancies,
  })
  const fareClass = persistedFareClass && (!persistedBaseFareClass || persistedBaseFareClass === policyFareClass)
    ? persistedFareClass
    : policyFareClass ?? FlightsClassTypes.ECONOMY

  return {
    offer,
    charterFlightBundle: {
      charterBundleRoute: route,
      originAirportCode: persistedBundle?.originAirportCode ?? routeOriginAirportCode ?? DEFAULT_CHARTER_ORIGIN_AIRPORT_CODE,
      fareClass,
      campaignTags: persistedBundle?.campaignTags,
    },
    occupancies: effectiveOccupancies,
  }
}

/**
 * Match the charter departing/returning journeys to the stay dates. The return
 * is restricted to fares paired with the chosen departure via `pairedFareId` —
 * the same pairing the calendar (`useCharterDateRangesByMonth`) uses to build
 * valid ranges — so the two legs can't be matched into a combination the user
 * could never have selected. Falls back to all returns if pairing data is absent.
 *
 * `onewayFares` is leg-indexed: `[0]` = departing leg, `[1]` = returning leg.
 */
export function matchCharterJourneys(
  onewayFares: Array<Array<App.JourneyV2>> | undefined,
  checkInDate: string | undefined,
  checkOutDate: string | undefined,
): { departingJourney?: App.JourneyV2; returningJourney?: App.JourneyV2 } {
  const departingFares = onewayFares?.at(0)
  const returningFares = onewayFares?.at(1)

  const departingJourney = checkInDate
    ? departingFares?.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        return arrivalDate && moment(arrivalDate).format(ISO_DATE_FORMAT) === checkInDate
      })
    : departingFares?.[0]

  const pairedFareIds = departingJourney?.pairedFareId
  const pairedReturns = pairedFareIds?.length
    ? returningFares?.filter(journey => pairedFareIds.includes(journey.id))
    : returningFares

  const returningJourney = checkOutDate
    ? pairedReturns?.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        return departingDate && moment(departingDate).format(ISO_DATE_FORMAT) === checkOutDate
      })
    : pairedReturns?.[0]

  return { departingJourney, returningJourney }
}

/**
 * Resolve the fare class to send to /flightsearch. Single occupancy Economy
 * Extra (sky lounge) bookings use the dedicated pax-one fare. Mirrors
 * `useAllCharterFlightJourneys`.
 */
export function toEffectiveCharterFareClass(
  fareClass: FareClassWithReward,
  occupancies: Array<App.Occupants>,
): FlightsClassTypes {
  const baseFareClass = toBaseFareClass(fareClass)
  if (baseFareClass === FlightsClassTypes.LUXROW && isSkyLoungeSingleOccupancy(occupancies)) {
    return FlightsClassTypes.LUXROW_PAX_ONE
  }
  return baseFareClass
}

export type CharterBundleHydrationResult =
  | { ok: true; flightItems: Array<App.Checkout.FlightItem> }
  | { ok: false; reason: 'no_journey' | 'no_fare' | 'insufficient_seats' | 'search_failed' | 'invalid_selection' | 'generation_failed' }

interface FetchCharterBundleFlightItemsArgs {
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  charterFlightBundle: CharterFlightBundleSelection
  /** The checkout accommodation item(s) the flight legs bundle with. */
  accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem>
  /** Stay dates — taken from the parent accommodation item (kept fresh). */
  checkIn: string | undefined
  checkOut: string | undefined
  /** Occupancies — taken from the parent accommodation item. */
  occupancies: Array<App.Occupants>
  region: string
  currency: string
  email?: string
}

/**
 * Imperatively derive the charter (+ feeder) flight checkout items for a saved
 * charter-bundle accommodation item, replicating the offer-page flow
 * (`useCharterFlightJourneysForHotelDates` + `generateAccommodationBundleFlightItems`)
 * without React. Used by the chat trip-planner multi-cart checkout so the
 * bundled flight is carried through exactly as it is from the offer page.
 */
export async function fetchCharterBundleFlightItems({
  offer,
  charterFlightBundle,
  accommodationItems,
  checkIn,
  checkOut,
  occupancies,
  region,
  currency,
  email,
}: FetchCharterBundleFlightItemsArgs): Promise<CharterBundleHydrationResult> {
  const { charterBundleRoute } = charterFlightBundle
  if (!isCharterBundleRoute(charterBundleRoute)) return { ok: false, reason: 'search_failed' }
  const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]

  if (!isValidFareClassWithReward(charterFlightBundle.fareClass)) {
    return { ok: false, reason: 'invalid_selection' }
  }
  const fareClass: FareClassWithReward = charterFlightBundle.fareClass
  const baseFareClass = toBaseFareClass(fareClass)

  const airportCode = charterFlightBundle.originAirportCode
  if (airportCode !== routeInfo.originAirport.airportCode && !isConfiguredFeederOriginAirportCode(charterBundleRoute, airportCode)) {
    return { ok: false, reason: 'invalid_selection' }
  }
  const flightOrigin = { code: airportCode } as App.Airport
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)
  const effectiveFareClass = toEffectiveCharterFareClass(fareClass, occupancies)

  let searchResult
  try {
    searchResult = await searchFlights({
      itinerary: [
        {
          departureAirport: routeInfo.originAirport.airportCode ?? '',
          arrivalAirport: routeInfo.destinationAirport.airportCode ?? '',
          departureDate: routeInfo.dateRange.min,
        },
        {
          departureAirport: routeInfo.destinationAirport.airportCode ?? '',
          arrivalAirport: routeInfo.originAirport.airportCode ?? '',
          departureDate: routeInfo.dateRange.max,
        },
      ],
      occupancies,
      fareClass: effectiveFareClass,
      source: CHARTER,
      currency,
      region,
      email,
      ...(feederOriginAirportCode ? { feederOriginAirportCode } : {}),
    })
  } catch {
    return { ok: false, reason: 'search_failed' }
  }

  const { departingJourney, returningJourney } = matchCharterJourneys(
    searchResult.onewayFares,
    checkIn,
    checkOut,
  )
  if (!departingJourney || !returningJourney || !searchResult.searchId || !searchResult.timestamp) {
    return { ok: false, reason: 'no_journey' }
  }

  // Block when the charter cannot seat all paying passengers, mirroring the
  // offer page (OfferCheckoutButton) rather than silently dropping the flight.
  const flightOccupants = countOccupantsForFlights(occupancies)
  const seatEligiblePassengers = flightOccupants.adults + flightOccupants.children + flightOccupants.teenagers
  const departingSeatsLeft = getSeatsLeftFromJourney(departingJourney)
  const returningSeatsLeft = getSeatsLeftFromJourney(returningJourney)
  const charterSeatsLeft = [departingSeatsLeft, returningSeatsLeft]
    .filter((seats): seats is number => typeof seats === 'number')
    .reduce<number | undefined>((min, seats) => (min === undefined ? seats : Math.min(min, seats)), undefined)
  if (typeof charterSeatsLeft === 'number' && charterSeatsLeft < seatEligiblePassengers) {
    return { ok: false, reason: 'insufficient_seats' }
  }

  let fareFamilies: FareFamiliesResponse
  try {
    fareFamilies = await getFlightFareFamilies({
      searchId: searchResult.searchId,
      sessionId: '',
      origin: departingJourney.flightGroup.flights[0].departureAirport,
      destination: returningJourney.flightGroup.flights[0].departureAirport,
      occupancies: mergeOccupants([flightOccupants]),
      region,
      isStandalone: false,
      selectedFareIds: [departingJourney.id, returningJourney.id],
      feederOriginAirportCode,
      email,
    })
  } catch {
    return { ok: false, reason: 'search_failed' }
  }

  const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(
    fareFamilies,
    baseFareClass,
  )
  if (!departingFareFamily || !returningFareFamily) {
    return { ok: false, reason: 'no_fare' }
  }

  let flightItems: Array<App.Checkout.FlightItem>
  try {
    flightItems = generateAccommodationBundleFlightItems({
      // The cart builder only reads occupant counts from `rooms`
      // (countOccupantsForFlights), so attach a synthetic id to satisfy
      // App.RoomOccupants without mutating the shared cart contract.
      rooms: occupancies.map((occupant, index) => ({ ...occupant, id: String(index) })),
      airport: flightOrigin,
      offer,
      accommodationItems,
      // The builder derives the feeder leg's `quotedFare` from `quotedFare -
      // charterFlightPrice`. We don't have the offer page's pre-summed total
      // here, so we leave this undefined and seed the feeder quote from its own
      // `bundleBudget.tripBudget` below instead (see comment there).
      quotedFare: undefined,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney: departingJourney,
      returningCharterFlightJourney: returningJourney,
      departingCharterFareFamily: departingFareFamily,
      returningCharterFareFamily: returningFareFamily,
      charterFlightSearchResultsTimestamp: searchResult.timestamp,
      charterFlightSearchId: searchResult.searchId,
    })
  } catch {
    return { ok: false, reason: 'generation_failed' }
  }

  if (flightItems.length === 0) return { ok: false, reason: 'no_fare' }

  // Seed the feeder leg's `quotedFare` from its own `bundleBudget.tripBudget`.
  // Checkout falls back to `quotedFare` for cart/payment totals while the feeder
  // is still pending selection (`totals.price || quotedFare || 0`), so without
  // this the feeder leg would carry 0 in the pending window — underquoting the
  // bundle. `bundleBudget.tripBudget` is the feeder estimate (sum of both legs'
  // `feederEstimatedPrice`), which is exactly the value the offer page lands on
  // via `quotedFare - charterFlightPrice`. Only seed when the builder left it
  // unset, to avoid clobbering any explicit quote.
  for (const item of flightItems) {
    if (item.quotedFare === undefined && item.bundleBudget) {
      item.quotedFare = item.bundleBudget.tripBudget
    }
  }

  return { ok: true, flightItems }
}

export type CharterBundleFlightPriceResult =
  | { ok: true; flightPrice: number }
  | { ok: false }

/**
 * Cash price of the charter bundle's flight legs (charter + feeder estimate) for
 * a given selection, for cart/itinerary display. Reuses fetchCharterBundleFlightItems
 * so the displayed price cannot diverge from what checkout actually builds: the
 * charter leg's quotedFare plus the feeder leg's budget.
 */
export async function fetchCharterBundleFlightPrice(
  args: Omit<FetchCharterBundleFlightItemsArgs, 'accommodationItems'>,
): Promise<CharterBundleFlightPriceResult> {
  const result = await fetchCharterBundleFlightItems({
    ...args,
    // Pricing-only probe — only `itemId` is read (for bundledItemIds), which
    // doesn't affect the computed fare.
    accommodationItems: [{ itemId: 'charter-price-probe' } as App.Checkout.InstantBookingLEHotelItem],
  })
  if (!result.ok) return { ok: false }
  // Identify the legs by the fields the cart builder sets, not array position:
  // the charter leg carries `charterBundleRoute`, the feeder leg carries
  // `bundleBudget`. Robust to ordering changes and to the feeder leg being absent
  // (origin === charter departure airport, so no feeder is generated).
  const charterItem = result.flightItems.find((item) => item.charterBundleRoute)
  if (!charterItem) return { ok: false }
  const feederItem = result.flightItems.find((item) => item.bundleBudget)
  const flightPrice = (charterItem.quotedFare ?? 0) + (feederItem?.bundleBudget?.tripBudget ?? 0)
  return { ok: true, flightPrice }
}
